import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ShieldCheck, MessageSquare, Award, Zap, Globe, Users, Target } from 'lucide-react';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <ShieldCheck className="w-12 h-12 text-cyan-400" />,
      title: 'Unwavering Trust',
      description: 'Built on a foundation of integrity and verified success, we ensure every placement is secure and reliable.'
    },
    {
      icon: <MessageSquare className="w-12 h-12 text-cyan-400" />,
      title: 'Straightforward Approach',
      description: 'No hidden complexities. We provide clear, honest guidance through every stage of your global career transition.'
    },
    {
      icon: <Award className="w-12 h-12 text-cyan-400" />,
      title: 'Human-Led. AI-Powered.',
      description: 'We combine advanced AI matching with deep human expertise to find not just a job, but the right career fit.'
    },
    {
      icon: <Target className="w-12 h-12 text-cyan-400" />,
      title: 'Results Driven',
      description: 'Our success is measured by the growth of our candidates and the impact they have on their new communities.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Navbar />

      <main className="flex-grow pt-32">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />

          <div className="container mx-auto px-4 relative z-10 text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-cyan-400 font-black tracking-[0.3em] uppercase text-sm mb-6 block"
            >
              Our Ethos
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-8xl font-black text-white mb-8 leading-tight tracking-tight"
            >
              Trust is our<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Global Currency.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-400 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-medium"
            >
              Polyglots Talent was established to solve the impersonal nature of global recruitment.
              We guide professionals towards high-impact careers with radical transparency.
            </motion.p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-24 bg-slate-900/30">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="lg:w-1/2">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-8">The Polyglots Pulse</h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-8">
                  Global recruitment is often broken, impersonal, and opaque. We set out to change that by establishing a platform where **trust is the standard**.
                  We are more than a consultancy; we are your project manager for a new life.
                  Our expertise in German and European markets allows us to navigate complexities so you don't have to.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-12">
                  <div className="bg-slate-800/50 p-8 rounded-3xl border border-white/5">
                    <div className="text-4xl font-black text-cyan-400 mb-2">01.</div>
                    <div className="text-xl font-bold text-white mb-2">Elite Partners</div>
                    <p className="text-sm text-slate-500">Only working with certified, high-impact medical and industrial institutions.</p>
                  </div>
                  <div className="bg-slate-800/50 p-8 rounded-3xl border border-white/5">
                    <div className="text-4xl font-black text-cyan-400 mb-2">02.</div>
                    <div className="text-xl font-bold text-white mb-2">Ethics First</div>
                    <p className="text-sm text-slate-500">No hidden fees, no deceptive promises. We prioritize your long-term success.</p>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2 relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-slate-800 to-slate-900 p-1 rounded-[40px] shadow-2xl"
                >
                  <div className="bg-slate-950 p-12 rounded-[38px] overflow-hidden relative group">
                    <div className="absolute inset-0 bg-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    <h3 className="text-3xl font-black text-white mb-6 relative z-10">Our Commitment</h3>
                    <blockquote className="text-2xl font-bold text-slate-200 italic leading-snug mb-8 relative z-10">
                      "Your success is our reputation. We treat every application as if it were for our own family member."
                    </blockquote>
                    <div className="flex items-center gap-4 relative z-10">
                      <div className="w-16 h-1 bg-cyan-500 rounded-full" />
                      <span className="text-cyan-400 font-black uppercase tracking-widest text-sm">Polyglots Leadership</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Grid */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Our Foundational Pillars</h2>
              <p className="text-slate-500 text-lg max-w-2xl mx-auto">Why thousands of professionals trust us with their global career transition every year.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-900/50 backdrop-blur-sm border border-white/5 p-10 rounded-3xl hover:border-cyan-500/30 transition-all group"
                >
                  <div className="bg-slate-950/80 w-20 h-20 rounded-2xl flex items-center justify-center mb-8 border border-slate-800 shadow-xl group-hover:shadow-cyan-500/10 transition-all">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">{item.title}</h3>
                  <p className="text-slate-500 leading-relaxed font-medium">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gradient-to-b from-transparent to-cyan-500/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-8">Ready for a straightforward path?</h2>
            <p className="text-slate-400 text-xl mb-12">Start your journey with a partner you can trust.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.a
                href="/signup"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="h-16 px-10 bg-cyan-500 text-white font-black rounded-full flex items-center justify-center shadow-2xl shadow-cyan-500/20"
              >
                Start Your Application
              </motion.a>
              <motion.a
                href="/"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="h-16 px-10 border-2 border-slate-700 text-white font-black rounded-full flex items-center justify-center hover:bg-white/5"
              >
                Go Back Home
              </motion.a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;

